import {Alert, Button, Stack, Typography, useTheme} from "@mui/material";
import InvoiceGeneralData from "./invoiceGeneralData";
import InvoiceItemData from "./invoiceItemData";
import React, {useEffect, useImperativeHandle, useRef, useState} from "react";
import DinoDialog from "../../../components/Dialog";
import {cleanData} from "../../../utils/FormUtils";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import AddCircleRoundedIcon from '@mui/icons-material/AddCircleRounded';
import {generateUUID} from "../../../utils/utils";
import InvoiceItem from "./InvoiceItem";
import {thousandSeparator} from "../../../utils/NumberUtils";
import {currencies, paymentMethods, unitPriceTypes, units, vat} from "../../../utils/enums";
import useCallDataApi from "../../../hooks/data";
import Loading from "../../../components/Loading";
import useDataModel from "../../../hooks/dataModel";

const ComposeInvoice = ({referenceGeneral, referenceItems, obj}) => {
    const theme = useTheme()
    const [current, setCurrent] = useState(-1)
    const {fetchModel: fetchTourOffice, loading} = useDataModel(useCallDataApi('tour-offices'))
    const [sharedValues, setSharedValues] = useState({
        currency: obj.currency
    })
    const [open, setOpen] = useState(false)
    const [editOpen, setEditOpen] = useState(false)
    const [deleteOpen, setDeleteOpen] = useState(false)

    const getUnitPrice = () => {
        const price = obj?.price

        const multiplier = (parseInt(obj?.order_rx) || 1) * (obj?.days || 1)

        return Math.round(price / multiplier)
    }

    const initialState = [{
        uid: generateUUID(),
        name: "Headset bérlés",
        unit_price: getUnitPrice()?.toString() * (obj?.days || 1),
        unit_price_type: unitPriceTypes.BRUTTO,
        payment_method: paymentMethods.WIRE_TRANSFER,
        unit: units.PIECE,
        quantity: (parseInt(obj?.order_rx) || 1),
        vat: vat.NORMAL,
        comment: `Dátumok: ${obj?.delivery_date || '-'} - ${obj?.return_date || '-'}\nMegrendelés száma: ${obj?.order_number}`
    }]

    if (obj?.is_urgent_order) initialState.push({
        uid: generateUUID(),
        name: "Sürgősségi felár",
        unit_price: Math.round(obj.price * 0.2),
        payment_method: paymentMethods.WIRE_TRANSFER,
        unit_price_type: unitPriceTypes.BRUTTO,
        unit: units.PIECE,
        quantity: 1,
        vat: vat.NORMAL,
        comment: '20% sürgősségi felár.'
    })

    useEffect(() => {
        fetchTourOffice(obj?.tour_office, to => {
            if (to?.discount_percent) initialState.push({
                uid: generateUUID(),
                name: "Kedvezmény",
                unit_price: obj.price * (to.discount_percent / 100) * -1,
                payment_method: paymentMethods.WIRE_TRANSFER,
                unit_price_type: unitPriceTypes.BRUTTO,
                unit: units.PIECE,
                quantity: 1,
                vat: vat.NORMAL,
                comment: `${to.discount_percent}% partner kedvezmény.`
            })
            setItems(initialState)
        })
    }, [])

    const [items, setItems] = useState(initialState)

    const itemForm = useRef(null)

    const findItem = uuid => items.find(item => item?.uid === uuid)

    const addItem = data => setItems([...items, {...data, uid: generateUUID()}])

    const editItem = (uid, newData) => setItems(items.map(item => item.uid === uid ? {...item, ...newData} : item))

    const deleteItem = uid => setItems(items.filter(item => item.uid !== uid))

    const getItems = () => items.map(({ uid, ...rest }) => rest)

    useImperativeHandle(referenceItems, () => ({
        getItems
    }))

    const handleEdit = uid => {
        setCurrent(uid)
        setEditOpen(true)
    }

    const handleDelete = uid => {
        setCurrent(uid)
        setDeleteOpen(true)
    }

    const calculateTotal = () => items.reduce((total, { quantity, unit_price }) => total + (quantity * unit_price), 0)

    return <Stack spacing={2}>
        {obj?.is_urgent_order && <Alert severity="error" variant="filled">
            Sürgős rendelés! A 20% sürgősségi felár hozzá lett adva a tételekhez.
        </Alert>}

        <Stack spacing={2} sx={{border: `1px solid ${theme.palette.primary.main}`, borderRadius: 4, padding: 3}}>
            <Typography variant='h5'>Általános adatok:</Typography>
            <InvoiceGeneralData reference={referenceGeneral} obj={obj} sharedValues={sharedValues} setSharedValues={setSharedValues}/>
        </Stack>

        <Stack spacing={2} sx={{border: `1px solid ${theme.palette.primary.main}`, borderRadius: 4, padding: 3}}>
            <Typography variant='h5'>Tételek:</Typography>
            {items.map(item => <InvoiceItem
                key={item.uid}
                item={item}
                edit={() => handleEdit(item.uid)}
                delete={() => handleDelete(item.uid)}
                sharedValues={sharedValues}
                setSharedValues={setSharedValues}
            />)}
            <Typography variant='h5'>Összesen: {thousandSeparator(calculateTotal())} {sharedValues?.currency}</Typography>
            <Button endIcon={<AddCircleRoundedIcon/>} variant='outlined' color='success' onClick={() => setOpen(true)}>Tétel hozzáadása</Button>
        </Stack>


        <DinoDialog
            handleClose={() => setOpen(false)}
            actions={<Stack direction='row' spacing={2} marginRight={2} marginBottom={2}>
                <Button size='large' variant='outlined'
                        onClick={() => setOpen(false)}>Mégsem</Button>
                <Button size='large' variant='outlined' color='success' onClick={() => {
                    const data = cleanData(itemForm.current.getData())
                    if (Object.entries(data).length > 0) {
                        addItem(data)
                        setOpen(false)
                    }
                }} endIcon={<SaveRoundedIcon/>}>Mentés</Button>
            </Stack>}
            open={open}
            title='Tétel hozzáadása'
            subtitle='Tétel hozzáadása'>
            <InvoiceItemData reference={itemForm}/>
        </DinoDialog>


        <DinoDialog
            handleClose={() => setEditOpen(false)}
            actions={<Stack direction='row' spacing={2} marginRight={2} marginBottom={2}>
                <Button size='large' variant='outlined' onClick={() => setEditOpen(false)}>Mégsem</Button>
                <Button size='large' variant='outlined' color='success' onClick={() => {
                    const data = cleanData(itemForm.current.getData())
                    if (Object.entries(data).length > 0) {
                        editItem(current, data)
                        setEditOpen(false)
                    }
                }} endIcon={<EditRoundedIcon/>}>Szerkesztés</Button>
            </Stack>}
            open={editOpen}
            title='Tétel szerkesztése'
            subtitle='Tétel szerkesztése'>
            <InvoiceItemData reference={itemForm} obj={findItem(current)}/>
        </DinoDialog>


        <DinoDialog
            handleClose={() => setDeleteOpen(false)}
            actions={<Stack direction='row' spacing={2} marginRight={2} marginBottom={2}>
                <Button size='large' variant='outlined' onClick={() => setDeleteOpen(false)}>Mégsem</Button>
                <Button size='large' variant='outlined' color='error' onClick={() => {
                    deleteItem(current)
                    setDeleteOpen(false)
                }} endIcon={<DeleteRoundedIcon/>}>Törlés</Button>
            </Stack>}
            open={deleteOpen}
            title='Tétel törlése'
            subtitle='Ez a művelet nem vonható vissza!'
        />

        <Loading isLoading={loading}/>
    </Stack>
}

export default ComposeInvoice